import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { DragDropContext } from "react-beautiful-dnd";
import TaskGroup from "./TaskGroup";
import useMessageBox from "@maxis/client-logic/hooks/useMessageBox";
import { initGroupData } from "./constants/initData";
import { KanbanBoardContainer } from "./style/style";
import "./style/style.css";
import uuid from "react-uuid";
import KanBanPopup from "./popup/KanBanPopup";

const KanbanBoard = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(KanbanBoardPropTypes).includes(p)) {
      console.error(`KanbanBoard: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [orgGroup, setOrgGroup] = useState([]);

  const kanBanPopupRef = useRef();
  const { alert } = useMessageBox();

  React.useImperativeHandle(ref, () => ({
    // 최초 group(JSON) 기준으로 kanban form setting
    initGroups(params) {
      try {
        if (params.length === 0) params = initGroupData;
        const transParam = params.map((x) => {
          return addGroup(x);
        });
        let tmpGroups = {};

        transParam.forEach((group) => {
          tmpGroups[group.id] = group;
        });

        setGroups(tmpGroups);
        setOrgGroup(transParam);
      } catch (error) {
        alert("error", error.message);
      }
    },

    setDataBinding(params) {
      _setDataBinding(params);
    },
    getTasks() {
      let tmpTasks = JSON.parse(JSON.stringify(tasks));
      Object.keys(groups).map((key) => {
        let group = groups[key];
        group.taskIds.map((taskId) => {
          tmpTasks[taskId].groupId = group.id;
        });
      });

      return tmpTasks;
    },
    kanbanAddPopup() {
      kanBanPopupRef.current?.showOpen("task 등록", true);
    },
    kanbanModifyPopup(e) {
      kanBanPopupRef.current?.showOpen("task 수정", false, e);
    },
  }));

  /*
   * data binding
   */
  const _setDataBinding = (params) => {
    params.forEach((x) => (x.id = x.id ? x.id : uuid()));

    if (validationId(params)) {
      alert("", "task data에서 id 중복되는 값이 존재합니다.");
      return;
    }

    Object.keys(groups).map((key) => {
      const group = groups[key];
      group.taskIds = params.filter((task) => task.groupId === group.id).map((task) => task.id);
    });

    let tmpTasks = {};

    params.forEach((task) => {
      tmpTasks[task.id] = task;
    });

    setTasks(tmpTasks);
  };

  /*
   * 만들어질 group의 base form
   */
  const addGroup = ({ id, title }) => {
    return {
      id: id,
      title: title,
      taskIds: [],
    };
  };

  /*
   * task data에서 id 중복되는 값 체크
   */
  const validationId = (arr) => {
    const idMap = {};
    arr.forEach((x) => {
      idMap[x.id] = (idMap[x.id] || 0) + 1;
    });

    return Object.values(idMap).some((count) => count > 1);
  };

  /*
   * task를 drag & drop 시 실행되는 func
   * 현재 drag & drop를 빠르게 실행할 시, 오류가 발생함 : react 18 version 으로 인한 오류로 추측
   */
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    /*
     * 최초 state 저장
     */
    const prevResult = {
      groups: JSON.parse(JSON.stringify(groups)),
      tasks: JSON.parse(JSON.stringify(tasks)),
    };

    /*
     * 대상 data 저장
     */
    const params = {
      prevData: JSON.parse(JSON.stringify(tasks[draggableId])),
      nextData: {},
    };

    const startGroup = groups[source.droppableId];
    const finishGroup = groups[destination.droppableId];

    // 한 droppable 내에서 움직이는 로직( group 내의 이동 시 )
    if (source.droppableId === destination.droppableId) {
      const newTaskIds = Array.from(startGroup.taskIds);

      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);

      /*
       * 바뀐 tasks를 groups.taskIds에 적용
       */
      const newGroup = {
        ...startGroup,
        taskIds: newTaskIds,
      };

      let newGroups = { ...groups };
      newGroups[source.droppableId] = newGroup;
      setGroups(newGroups);

      /*
       * 바뀐 tasks 적용
       */
      dataControl.chgTasks(newGroups, draggableId, destination.droppableId, prevResult, params);

      return;
    }

    // 다른 droppable로 옮기기 ( 타 group 으로 옮길 시 )
    const startTaskIds = Array.from(startGroup.taskIds);
    startTaskIds.splice(source.index, 1);
    const newStartGroup = {
      ...startGroup,
      taskIds: startTaskIds,
    };

    const finishTaskIds = Array.from(finishGroup.taskIds);
    finishTaskIds.splice(destination.index, 0, draggableId);
    const newFinishGroup = {
      ...finishGroup,
      taskIds: finishTaskIds,
    };

    /*
     * 바뀐 tasks를 groups.taskIds에 적용
     */
    let newGroups = { ...groups };
    newGroups[source.droppableId] = newStartGroup;
    newGroups[destination.droppableId] = newFinishGroup;

    setGroups(newGroups);

    /**
     * 바뀐 tasks 적용
     */
    dataControl.chgTasks(newGroups, draggableId, destination.droppableId, prevResult, params);
  };

  const dataControl = {
    saveData: async (param) => {
      try {
        /*
         * 최초 state 저장
         */
        const prevResult = {
          groups: JSON.parse(JSON.stringify(groups)),
          tasks: JSON.parse(JSON.stringify(tasks)),
        };

        // 추가된 데이터(task)를 STATE groups.taskIds에 넣기 위한 세팅
        const newGroup = groups[param.groupId];
        const newTaskIds = Array.from(newGroup.taskIds);
        newTaskIds.splice(newTaskIds.length, 0, param.id);
        newGroup.taskIds = newTaskIds;

        let newGroups = { ...groups };
        newGroups[newGroup.id] = newGroup;
        setGroups(newGroups);

        // 추가된 데이터(task)를 STATE tasks에 넣기 위한 세팅
        const newTasks = { ...tasks };
        newTasks[param.id] = param;
        setTasks(newTasks);

        if (props.latestData) {
          // 부모창 state 최신화
          await props.latestData(Object.values(newTasks));
        }

        /*
         * 현재 변경된 state 저장
         */
        const nowResult = { groups: newGroups, tasks: newTasks };

        if (props.saveAction) {
          // 부모창으로, 저장된 data info return
          await props.saveAction(await dataControl.rtnData(prevResult, nowResult, param, "I"));
        }
      } catch (e) {
        alert("error", e.message);
      }
    },
    modifyData: async (param) => {
      try {
        const data = tasks[param.id];

        /*
         * 최초 state 저장
         */
        const prevResult = {
          groups: JSON.parse(JSON.stringify(groups)),
          tasks: JSON.parse(JSON.stringify(tasks)),
        };

        /*
         * 대상 data 저장
         */
        const params = {
          prevData: JSON.parse(JSON.stringify(data)),
          nextData: param,
        };

        /*
         * 변경된 데이터 세팅
         */
        data.groupId = param.groupId;
        data.content = param.content;
        data.customData.valueNow = param.customData.valueNow;

        /*
         * 수정된 데이터가 groupId 자체가 변경 되었을 시,
         * 변경된 데이터가 해당 그룹의 최하위로 가도록 세팅
         * group이 변경되지 않았다면 현재 위치에서 값만 변경되기 위해 if를
         */
        if (param.groupId !== params.prevData.groupId) {
          // 기존 group에서 옮겨진 data를 제외한 값 return
          const groupTasks = groups[params.prevData.groupId].taskIds.filter((x) => {
            return x !== param.id;
          });

          // 기존 group에서 옮겨진 data를 제외한 값 group에 세팅
          groups[params.prevData.groupId].taskIds = groupTasks;
          // 옮겨진 data group에 세팅
          groups[param.groupId].taskIds.push(param.id);

          // 1. 새로 옮겨진 data의 group의 id 순서 ( 새로 옮겨지면 task의 마지막 줄에 가도록 )
          const standardInfo = groups[param.groupId].taskIds;
          const changeTasks = {};

          // 2. 기준에 의한 순서로 재배치 ( changeTasks 에 세팅 )
          standardInfo.forEach((x) => {
            const taskTemp = tasks[x];
            delete tasks[x];
            if (taskTemp) changeTasks[x] = taskTemp;
          });

          const newTasks = { ...tasks, ...changeTasks };

          // 3. 값 세팅
          setTasks(newTasks);

          if (props.latestData) {
            // 부모창 state 최신화
            await props.latestData(Object.values(newTasks));
          }
        } else {
          if (props.latestData) {
            // 부모창 state 최신화
            await props.latestData(Object.values(tasks));
          }
        }

        /*
         * 현재 변경된 state 저장
         */
        const nowResult = { groups: groups, tasks: tasks };

        if (props.modifyAction) {
          // 부모창으로, 수정된 data info return
          await props.modifyAction(await dataControl.rtnData(prevResult, nowResult, params, "U"));
        }
      } catch (e) {
        alert("error", e.message);
      }
    },
    deleteData: async (param) => {
      /*
       * 최초 state 저장
       */
      const prevResult = {
        groups: JSON.parse(JSON.stringify(groups)),
        tasks: JSON.parse(JSON.stringify(tasks)),
      };

      // 삭제될 task를 params 변수에 담기
      const params = JSON.parse(JSON.stringify(tasks[param.id]));

      // task 삭제
      delete tasks[param.id];

      // groups.taskIds에서 삭제된 task 제외하고 newTasks 변수에 담기
      const newTasks = groups[param.groupId].taskIds.filter((x) => {
        return x !== param.id;
      });

      // groups 세팅
      groups[param.groupId].taskIds = newTasks;

      if (props.latestData) {
        // 부모창 state 최신화
        await props.latestData(Object.values(tasks));
      }

      /*
       * 현재 변경된 state 저장
       */
      const nowResult = { groups: groups, tasks: tasks };
      if (props.deleteAction) {
        // 부모창으로, 삭제된 data info return
        await props.deleteAction(await dataControl.rtnData(prevResult, nowResult, params, "D"));
      }

      _setDataBinding(Object.values(tasks));
    },
    /*
     * 팝업창에서 저장, 수정된 데이터에 대한 call back
     */
    popupCallBack: async (isNew, param, delMode) => {
      if (!param) return;

      const nowMode = await dataControl.nowMode(isNew, delMode);
      if (nowMode === "I") {
        dataControl.saveData(param);
      } else if (nowMode === "U") {
        dataControl.modifyData(param);
      } else {
        dataControl.deleteData(param);
      }
    },
    nowMode: async (isNew, delMode) => {
      if (delMode) {
        return "D";
      } else {
        if (isNew) {
          return "I";
        } else {
          return "U";
        }
      }
    },
    /*
     * 바뀐 tasks 적용
     * task가 group 내에서 이동 또는 다른 group으로 이동 시, 데이터 최신화를 위한 작업
     * task가 다른 group으로 이동 시, 사용자가 위치한 곳으로 배치되도록 설정.
     * 수정된 group의 tasksIds 위치를 기준으로 tasks를 세팅
     * 해당 func를 실행하지 않을 시, 이동 후 조회를 눌렀을 때 데이터의 위치가 기존의 위치를 따라가서 상이해짐.
     */
    chgTasks: async (newGroups, draggableId, droppableId, prevResult, params) => {
      tasks[draggableId].groupId = droppableId;

      /*
       * standardInfo : 수정된 그룹의 tasksIds 위치를 기준
       * baseTasks : 현재 수정된 tasks .. tasks의 위치 또는 값을 변경해야함
       * changeTasks: standardInfo의 값을 기준으로, 변경된 tasks 위치 또는 값을 세팅
       */
      const standardInfo = newGroups[droppableId].taskIds;
      const baseTasks = tasks;
      const changeTasks = {};

      // standardInfo 순서를 기준으로 baseTasks 값을 changeTasks에 세팅
      standardInfo.forEach((x) => {
        const task = baseTasks[x];
        delete baseTasks[x];

        if (task && task.groupId === droppableId) {
          changeTasks[x] = task;
        }
      });

      const newTasks = { ...baseTasks, ...changeTasks };

      // tasks 순서 최신화
      setTasks(newTasks);

      if (props.latestData) {
        // 부모창 state 최신화
        await props.latestData(Object.values(newTasks));
      }

      /*
       * 현재 변경된 state 저장
       */
      const nowResult = { groups: newGroups, tasks: newTasks };
      params.nextData = newTasks[draggableId];

      if (props.moveAction) {
        // 부모창으로, 이동된 data info return
        await props.moveAction(await dataControl.rtnData(prevResult, nowResult, params, "M"));
      }
    },
    /*
     * 부모창으로 return 할 data form setting
     */
    rtnData: async (prevResult, nowResult, param, mode) => {
      const data = {
        prevResult: prevResult,
        nowResult: nowResult,
        newData: {},
        modifyData: {},
        deleteData: {},
        mode: mode,
      };

      if (mode === "I") {
        data.newData = param;
      } else if (mode === "U" || mode === "M") {
        data.modifyData = param;
      } else if (mode === "D") {
        data.deleteData = param;
      }

      return data;
    },
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <KanbanBoardContainer className="task_wrap">
          {Object.keys(groups).map((key) => {
            const group = groups[key];
            const groupTasks = group.taskIds.map((taskId) => tasks[taskId]);
            return <TaskGroup onDoubleClick={props.onDoubleClick} key={group.id} group={group} tasks={groupTasks} />;
          })}
        </KanbanBoardContainer>
      </DragDropContext>
      <KanBanPopup ref={kanBanPopupRef} callBackAction={dataControl.popupCallBack} orgGroups={orgGroup} />
    </>
  );
});

const KanbanBoardPropTypes = {
  /*
   * 부모창 조회 func
   */
  onSearch: PropTypes.func,
  /*
   * 부모창 state 최신화 func
   */
  latestData: PropTypes.func,
  /*
   * 마우스 더블클릭 이벤트
   */
  onDoubleClick: PropTypes.func,
  /*
   * data 추가 시, 변경된 데이터 info func
   */
  saveAction: PropTypes.func,
  /*
   * data 수정 시, 변경된 데이터 info func
   */
  modifyAction: PropTypes.func,
  /*
   * data 삭제 시, 변경된 데이터 info func
   */
  deleteAction: PropTypes.func,
  /*
   * data group 이동 시( 같은 group 내 이동, 타 group 으로 이동 ), 변경된 데이터 info func
   */
  moveAction: PropTypes.func,
};

KanbanBoard.propTypes = KanbanBoardPropTypes;
KanbanBoard.defaultProps = {
  onSearch: null,
  latestData: null,
  onDoubleClick: null,
  saveAction: null,
  modifyAction: null,
  deleteAction: null,
  moveAction: null,
};

KanbanBoard.displayName = "KanbanBoard";
export default KanbanBoard;
